/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: MPL-2.0
 */

import classNames from 'classnames'
import Image from 'next/image'
import Alert from '@hashicorp/react-alert'
import { Button } from '@hashicorp/react-mds/src/components/button'
import useProductMeta, { Products } from '@hashicorp/platform-product-meta'
import ArrowIcon from './arrow-icon'
import { ComponentProps } from 'react'
import s from './style.module.css'
import { ThemeBackground } from '@hashicorp/react-button/types'

/**
 * Eliminate orphans from the end of a string, replacing spaces with &nbsp;
 * @param {string} string - The string to be de-orphaned
 * @param {string} [count = 12] - Total char count from end of string in which to replace spaces
 */
export const eliminateOrphans = (string: string, count?: number) => {
	const charArray = string.split(''),
		charArrayEnd = charArray.splice(-(count || 12)).map((char) => {
			return char.match(/\s/) ? '&nbsp;' : char
		})

	return charArray.concat(charArrayEnd).join('')
}

interface HeroProps {
	data: {
		title?: string
		description?: string
		centered?: boolean
		buttons?: ComponentProps<typeof Button>[]
		alert?: { url?: string; tag?: string; text?: string }
		backgroundImage?: {
			url: string
			width?: number
			height?: number
			alt?: string
		}
		backgroundTheme?: string
		helpText?: string
		image?: {
			url: string
			width?: number
			height?: number
			alt?: string
		}
		product?: Products
		smallTextTag?: string
		titleLogo?: {
			url: string
			width?: number
			height?: number
			alt?: string
		}
	}
	centered?: unknown
	className?: string
}

/**
 * A flexible hero component used as the primary content at the top of many HashiCorp pages.
 */
function Hero({ data, centered, className }: HeroProps) {
	const {
		alert,
		backgroundImage,
		backgroundTheme = 'dark',
		buttons,
		description,
		helpText,
		image,
		product,
		smallTextTag,
		title,
		titleLogo,
	} = data
	const { themeClass } = useProductMeta(product)
	const h1OrphanCount = 9
	const isCentered = centered || data.centered

	return (
		<div
			className={classNames(
				s.root,
				s[backgroundTheme],
				themeClass,
				{ [s.centered]: isCentered },
				className
			)}
		>
			{backgroundImage && (
				<Image
					className={s.background}
					alt={backgroundImage.alt}
					src={backgroundImage.url}
					{...backgroundImage}
				/>
			)}
			<div
				className={classNames(s.container, {
					[s.centered]: isCentered,
				})}
			>
				<div className={s.headline}>
					{smallTextTag && <div className="tag">{smallTextTag}</div>}
					{titleLogo && (
						<Image
							className={classNames(s.headlineLogo, {
								[s.centered]: isCentered,
							})}
							alt={titleLogo.alt}
							src={titleLogo.url}
							{...titleLogo}
						/>
					)}
					{alert && (
						<Alert
							url={alert.url}
							tag={alert.tag}
							text={alert.text}
							textColor={backgroundTheme === 'light' ? 'dark' : 'light'}
							className={s.headlineAlert}
						/>
					)}
					{title && (
						<h1
							className={classNames(s.headlineTitle, s[backgroundTheme])}
							dangerouslySetInnerHTML={{
								__html: eliminateOrphans(title, h1OrphanCount),
							}}
						/>
					)}
					{description && (
						<div
							className={classNames(s.headlineDescription, s[backgroundTheme])}
							dangerouslySetInnerHTML={{
								__html: eliminateOrphans(description.trim()),
							}}
						/>
					)}
					{buttons && buttons.length ? (
						<div
							className={classNames(s.headlineButtons, {
								[s.centered]: isCentered,
							})}
						>
							{buttons.map((button, idx) => {
								const hasZeroPadding =
									button.theme?.variant === 'tertiary' ||
									button.theme?.variant === 'tertiary-neutral' ||
									button.theme?.variant === 'ghost'
								return (
									<Button
										key={button.title}
										className={classNames(s.headlineButton, {
											[s.hasZeroPadding]: hasZeroPadding,
											[s.centered]: isCentered,
										})}
										{...button}
										theme={{
											brand: idx === 0 ? product : 'neutral',
											variant: idx === 0 ? 'primary' : 'secondary',
											background: (idx === 0
												? 'light'
												: product
												? backgroundTheme
												: 'dark') as ThemeBackground,
											...button.theme,
										}}
									/>
								)
							})}
						</div>
					) : null}
					{helpText && (
						<div
							className={classNames(s.headlineHelpText, s[backgroundTheme], {
								[s.centered]: isCentered,
							})}
						>
							<div dangerouslySetInnerHTML={{ __html: helpText.trim() }} />
							<ArrowIcon />
						</div>
					)}
				</div>
				{image ? (
					<div className={s.image}>
						<Image src={image.url} alt={image.alt || ''} {...image} />
					</div>
				) : null}
			</div>
		</div>
	)
}

export default Hero
